import type {
  AiLineItem,
  CampaignContextData,
  CreateLineItemsToolInput,
  DeleteLineItemToolInput,
  LineItemChatMessage,
  PrefillLineItemDraft,
  PromoteProposalToolInput,
  UpdateLineItemsToolInput
} from 'shared/src/api/ai-line-item-chat-types';
import {
  channelById,
  tacticById,
  unitPriceTypeById,
  type Channel
} from 'shared/src/domain/line-item/line-item-channels';
import { externalCommitOutcome } from '@/features/ai/shared/commit-card-frame';
import {
  effectivePlannedUnitCost,
  effectiveUnitPrice,
  perUnitFromQuoted,
  type CardSelection
} from './configuration-card';

// Line-item workspace logic that doesn't need the chat modal: workspace state derived from the chat
// history, the finalize prompt, and the post-create budget snapshot. Kept free of AI SDK imports so
// it can be unit tested.

export type LatestCreateCall = {
  toolCallId: string;
  lineItems: AiLineItem[];
  /** Remaining budget when the create was proposed; later turns report it after the create. */
  budget: number;
  state: string;
  output?: unknown;
};

export type LatestMutateCall = {
  toolCallId: string;
  tool: 'updateLineItems' | 'deleteLineItem' | 'promoteProposal';
  input: UpdateLineItemsToolInput | DeleteLineItemToolInput | PromoteProposalToolInput;
  state: string;
  output?: unknown;
};

export function extractWorkspaceData(messages: LineItemChatMessage[], channels: Channel[]) {
  let campaignContext: CampaignContextData | null = null;
  let latestCreateCall: LatestCreateCall | null = null;
  let latestMutateCall: LatestMutateCall | null = null;
  let remainingBudget = 0;
  const platformNameById: Record<number, string> = {};

  for (const ch of channels) {
    for (const t of ch.tactics) {
      for (const p of t.platforms) platformNameById[p.id] = p.name;
    }
  }

  for (const m of messages) {
    if (m.role !== 'assistant') continue;
    for (const part of m.parts) {
      if (part.type === 'data-campaign-context') {
        campaignContext = part.data as CampaignContextData;
        remainingBudget = campaignContext.remaining_budget;
      } else if (part.type === 'tool-createLineItems' && part.state !== 'input-streaming') {
        // No-execute confirmed write: the latest call drives the preview card. Skip
        // 'input-streaming' so the card doesn't flash with partially-streamed (possibly
        // undefined) fields; render only once input is complete (input-available onward).
        const input = part.input as CreateLineItemsToolInput | undefined;
        if (input?.line_items) {
          latestCreateCall = {
            toolCallId: part.toolCallId,
            lineItems: input.line_items,
            budget: remainingBudget,
            state: part.state,
            output: part.output
          };
        }
      } else if (part.type === 'tool-updateLineItems' && part.state !== 'input-streaming') {
        const input = part.input as UpdateLineItemsToolInput | undefined;
        if (input?.edits?.length) {
          latestMutateCall = {
            toolCallId: part.toolCallId,
            tool: 'updateLineItems',
            input,
            state: part.state,
            output: part.output
          };
        }
      } else if (part.type === 'tool-deleteLineItem' && part.state !== 'input-streaming') {
        const input = part.input as DeleteLineItemToolInput | undefined;
        if (input?.line_item_id) {
          latestMutateCall = {
            toolCallId: part.toolCallId,
            tool: 'deleteLineItem',
            input,
            state: part.state,
            output: part.output
          };
        }
      } else if (part.type === 'tool-promoteProposal' && part.state !== 'input-streaming') {
        latestMutateCall = {
          toolCallId: part.toolCallId,
          tool: 'promoteProposal',
          input: (part.input as PromoteProposalToolInput | undefined) ?? {},
          state: part.state,
          output: part.output
        };
      }
    }
  }

  return { campaignContext, latestCreateCall, latestMutateCall, remainingBudget, platformNameById };
}

// Finalize prompt for the create flow: describes every configured line item (the user may have built
// several) so the agent calls createLineItems ONCE with the full set, using these exact values.
export function buildProposePrompt(drafts: CardSelection[], channels: Channel[]): string {
  const lines = drafts.map((s, i) => {
    if (s.channelId === undefined || s.tacticId === undefined || s.priceTypeId === undefined) {
      return `- line item ${i + 1}: (incomplete)`;
    }
    const channel = channelById(channels, s.channelId);
    const tactic = tacticById(channels, s.tacticId);
    const priceType = unitPriceTypeById(channels, s.priceTypeId);
    const platformNames = s.platformIds
      .map(id => tactic.platforms.find(p => p.id === id)?.name)
      .filter((n): n is string => Boolean(n));
    const platformsLabel =
      s.platformIds.length > 0
        ? platformNames.join(', ')
        : 'none — the user selected no platforms; pass an empty array';
    // Resolve the unit price + planned unit cost here so the agent copies exact values instead of
    // computing them (which it gets wrong, e.g. 0, for some price types). The unit price is the
    // card's (rate-card default, or the user's entry when the rate card has none, e.g. SEM), already
    // per-unit; planned cost = unitPrice * (1 - targetMargin). Finalize is gated on both being set.
    const unitPrice = effectiveUnitPrice(s, channels);
    const plannedUnitCost = effectivePlannedUnitCost(s, channels);
    return [
      `- line item ${i + 1}:`,
      `  channel_id=${channel.id} (${channel.name})`,
      `  tactic_id=${tactic.id} (${tactic.name})`,
      `  unit_price_type_id=${priceType.id} (${priceType.name})`,
      `  media_platform_ids=[${s.platformIds.join(', ')}] (${platformsLabel})`,
      `  price=${s.price ?? 0}`,
      `  unit_price=${unitPrice}`,
      `  planned_unit_cost=${plannedUnitCost}`
    ].join('\n');
  });
  return [
    `The user has finalized ${drafts.length} line item${drafts.length > 1 ? 's' : ''} in the workspace card. Use EXACTLY these values:`,
    ...lines,
    `Call createLineItems ONCE with ${drafts.length === 1 ? 'this line item' : `all ${drafts.length} line items`}, one entry per line item above, each using its IDs, spanning all its media_platform_ids, with its exact price, unit_price, and planned_unit_cost. The unit_price and planned_unit_cost are already per-unit (set on the card) — use them verbatim; do NOT recompute or divide by 1000. Do not call presentOptions for these fields.`
  ].join('\n');
}

/** The drafts last sent for review, the remaining budget they were checked against, and the create
 * call already on screen at that moment (only a newer one can be the create for these drafts). */
export type FinalizedDrafts = {
  drafts: CardSelection[];
  budget: number;
  priorCreateCallId: string | null;
};

/** Once the finalized drafts are created, later turns report the remaining budget as $0. Keep
 * measuring those exact drafts (same array; any edit makes a new one) against the budget they were
 * created from, and report them as created so they can't be sent for creation twice. */
export function createdDraftsBudget(
  finalized: FinalizedDrafts | null,
  drafts: CardSelection[],
  latestCreateCall: LatestCreateCall | null,
  remainingBudget: number
): { draftsCreated: boolean; panelBudget: number } {
  const draftsCreated =
    finalized !== null &&
    finalized.drafts === drafts &&
    latestCreateCall !== null &&
    latestCreateCall.toolCallId !== finalized.priorCreateCallId &&
    externalCommitOutcome(latestCreateCall)?.status === 'applied';
  return { draftsCreated, panelBudget: draftsCreated ? finalized.budget : remainingBudget };
}

/** Drafts from an agent pre-fill. A unit price quoted in chat wins; otherwise a unit price the user
 * typed on that card survives while its tactic + price type are unchanged (same rule as the Zoho
 * hydration). */
export function prefillDrafts(
  prev: CardSelection[],
  lineItems: PrefillLineItemDraft[],
  channels: Channel[]
): CardSelection[] {
  const drafts = lineItems.map((li, i): CardSelection => {
    const draft: CardSelection = {
      channelId: li.channel_id,
      tacticId: li.tactic_id,
      priceTypeId: li.unit_price_type_id,
      platformIds: li.platform_ids ?? [],
      price: li.price
    };
    const before = prev[i];
    const unitPrice =
      li.quoted_unit_price !== undefined
        ? perUnitFromQuoted(li.quoted_unit_price, draft, channels)
        : before?.tacticId === draft.tacticId && before?.priceTypeId === draft.priceTypeId
          ? before.unitPrice
          : undefined;
    return { ...draft, unitPrice };
  });
  return drafts.length > 0 ? drafts : [{ platformIds: [] }];
}
